import React, { createContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SCORE_TYPE_NAME_FINAL_ID } from '../static/constants.js';
import { validateData } from '../components/FinalReview/finalReviewHelpers.js';

export const ScoreCellStateContext = createContext();
export const ScoreCellActionContext = createContext();

const ScoreCellRenderContextProvider = ({ children }) => {
  const [editResultType, setEditResultType] = useState(null);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [editedResults, setEditedResults] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [newScores, setNewScores] = useState({});
  const [results, setResults] = useState([]);

  const clearState = useCallback(() => {
    setDialogOpened(false);
    setEditResultType(null);
    setEditedResults([]);
    setIsDisabled(true);
    setNewScores({});
    setResults([]);
  }, []);

  const handleSelectScoreOption = useCallback((optionName, result) => {
    setEditedResults(prevEditedResults => {
      const _editedResults = JSON.parse(JSON.stringify(prevEditedResults));
      _editedResults.forEach((res) => {
        if (res.id === result.id) {
          let score = res.scores.find(s => s.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
          const scoreOption = result.scoreOptions.find(opt => opt.name === optionName);

          if (!score) {
            score = { scoreTypeId: SCORE_TYPE_NAME_FINAL_ID };
            res.scores.push(score);
          }

          score.scoreOption = scoreOption;
          score.scoreOptionId = scoreOption?.id || null;
          score.value = scoreOption ? result.weight * scoreOption.weight : 0;
        }
      });

      setIsDisabled(validateData(_editedResults));
      return _editedResults;
    });

    setNewScores(state => ({ ...state, [result.id]: {} }));
  }, []);

  const handleCommentChange = useCallback((comment, result) => {
    setEditedResults(prevEditedResults => {
      const _editedResults = JSON.parse(JSON.stringify(prevEditedResults));
      _editedResults.forEach((res) => {
        if (res.id === result.id) {
          let score = res.scores.find(s => s.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);

          if (!score) {
            score = { scoreTypeId: SCORE_TYPE_NAME_FINAL_ID };
            res.scores.push(score);
          }

          score.comments = comment;
        }
      });

      setIsDisabled(validateData(_editedResults));
      return _editedResults;
    });

    setNewScores(state => ({ ...state, [result.id]: {} }));
  }, []);

  const stateValue = useMemo(() => ({
    editResultType,
    dialogOpened,
    editedResults,
    isDisabled,
    newScores,
    results
  }), [
    editResultType,
    dialogOpened,
    editedResults,
    isDisabled,
    newScores,
    results
  ]);

  const actionValue = useMemo(() => ({
    setEditResultType,
    setDialogOpened,
    setEditedResults,
    setIsDisabled,
    setNewScores,
    setResults,
    clearState,
    handleSelectScoreOption,
    handleCommentChange
  }), [
    clearState,
    handleSelectScoreOption,
    handleCommentChange
  ]);

  return (
    <ScoreCellStateContext.Provider value={stateValue}>
      <ScoreCellActionContext.Provider value={actionValue}>
        {children}
      </ScoreCellActionContext.Provider>
    </ScoreCellStateContext.Provider>
  );
};

ScoreCellRenderContextProvider.propTypes = {
  children: PropTypes.node,
};

export default ScoreCellRenderContextProvider;
